/**
 * Shared section content renderer with markdown formatting (**, *, ~~, __).
 * Use in both section editor preview and results/output views for consistency.
 */
import React from 'react';

import { preprocessSectionContentForPreview } from '../../../../../utils/pdfTextSanitizer';

import { parseWorkExperienceContent, workExperienceEntriesToDisplayText } from "./workExperienceFields";

export function isBulletLine(line: string): boolean {
  const t = line.trimStart();
  if (/^[•\-](\s|$)/.test(t)) return true;
  if (/^\*\s/.test(t)) return true;
  return false;
}

export function bulletContent(line: string): string {
  const t = line.trimStart();
  const m = t.match(/^[•*\-]\s*(.*)$/);
  return m ? m[1].trim() : t;
}

export function isNumberedLine(line: string): boolean {
  const t = line.trimStart();
  return /^\d+\.\s+/.test(t) || /^\d+\)\s+/.test(t);
}

const INLINE_MARKERS: { open: string; close: string; Wrapper: React.FC<{ children: React.ReactNode }> }[] = [
  { open: '~~', close: '~~', Wrapper: ({ children }) => <span className="line-through" style={{ textDecoration: 'line-through' }}>{children}</span> },
  { open: '__', close: '__', Wrapper: ({ children }) => <span className="underline" style={{ textDecoration: 'underline' }}>{children}</span> },
  { open: '**', close: '**', Wrapper: ({ children }) => <strong style={{ fontWeight: 700 }}>{children}</strong> },
  { open: '*', close: '*', Wrapper: ({ children }) => <em className="italic" style={{ fontStyle: 'italic' }}>{children}</em> },
];

/** Render inline markdown: **bold**, *italic*, ~~strikethrough~~, __underline__. Parses outside-in so nesting works. */
export function renderInlineFormatting(text: string, lineKey: string): React.ReactNode {
  if (text == null || typeof text !== 'string') return text ?? '';
  const safeLineKey = typeof lineKey === 'string' ? lineKey : 'L';
  let partIdx = 0;
  const key = () => `${safeLineKey}-${partIdx++}`;
  const MAX_DEPTH = 20;

  function parse(seg: string, depth: number): React.ReactNode {
    if (depth > MAX_DEPTH || !seg) return seg || null;
    let earliest = -1;
    let markerIdx = -1;
    for (let i = 0; i < INLINE_MARKERS.length; i++) {
      const pos = seg.indexOf(INLINE_MARKERS[i].open);
      if (pos >= 0 && (earliest < 0 || pos < earliest)) {
        earliest = pos;
        markerIdx = i;
      }
    }
    if (earliest < 0) return seg;
    const m = INLINE_MARKERS[markerIdx];
    const before = seg.slice(0, earliest);
    const closeStart = seg.indexOf(m.close, earliest + m.open.length);
    if (closeStart < 0) {
      return (
        <>
          {before ? parse(before, depth + 1) : null}
          <React.Fragment key={key()}>{seg.slice(earliest)}</React.Fragment>
        </>
      );
    }
    const inner = seg.slice(earliest + m.open.length, closeStart);
    const after = seg.slice(closeStart + m.close.length);
    const Wrapper = m.Wrapper;
    return (
      <>
        {before ? parse(before, depth + 1) : null}
        <Wrapper key={key()}>{parse(inner, depth + 1)}</Wrapper>
        {after ? parse(after, depth + 1) : null}
      </>
    );
  }

  return <span className="inline">{parse(text, 0)}</span>;
}

interface SectionContentWithFormattingProps {
  content: string;
  className?: string;
  /** Optional section title hint for smarter rendering (e.g. skills list) */
  sectionTitle?: string;
}

/** True when section is a contact / personal info section */
function isContactSection(title?: string): boolean {
  return !!(title && /personal.?info|contact/i.test(title));
}

/** True when section is work experience so we normalize structure (company · title, dates, bullets) */
function isWorkExperienceSection(title?: string): boolean {
  return !!(title && /\b(work\s+experience|employment|experience|professional\s+experience)\b/i.test(title));
}

/**
 * Split a concatenated contact line into individual fields.
 * Handles LLM output like: "• email@x.com +91-6361... City https://linkedin..."
 * Strips the leading bullet character and splits on phone/URL/email boundaries.
 */
function expandContactLine(line: string): string[] {
  const text = isBulletLine(line) ? bulletContent(line) : line.trim();
  if (!text) return [];
  // Count how many contact-pattern types appear on this line
  const EMAIL_RE = /[\w._%+-]+@[\w.-]+\.[A-Za-z]{2,}/;
  const PHONE_RE = /\+?[\d][\d\s\-()]{6,}/;
  const URL_RE = /https?:\/\/\S+/;
  const matchCount = [EMAIL_RE, PHONE_RE, URL_RE].filter(p => p.test(text)).length;
  if (matchCount < 2) return [text]; // single field — return as-is
  // Split before phone numbers, URLs, and emails that follow a space
  return text
    .split(/(?<=\S)\s+(?=\+[\d])|(?<=\S)\s+(?=https?:\/\/)|(?<=\S)\s+(?=[\w._%+-]+@[\w.-]+\.[A-Za-z])/)
    .map(p => p.trim())
    .filter(Boolean);
}

const SKILLS_TITLE_RE = /\b(skill|technolog|tool|framework|language|platform|competen|proficien)/i;

/** True when section title indicates a skills/tech section */
function isSkillsSectionTitle(title?: string): boolean {
  return !!(title && SKILLS_TITLE_RE.test(title));
}

/**
 * Parse skills content into individual items.
 * Handles all common LLM output formats:
 *   "Java, Python, Go"                             (single comma-separated line)
 *   "Languages: Java, Python\nCloud: Docker, K8s"  (category-prefixed multi-line)
 *   "• Java\n• Python"                             (bullet list)
 */
function parseSkillsToItems(content: string): string[] {
  const items: string[] = [];
  const lines = content.split(/\n/);
  for (const rawLine of lines) {
    const line = rawLine.trim();
    if (!line) continue;
    // Strip leading bullet
    const noBullet = line.replace(/^[•\-*]\s*/, '');
    // Strip category prefix like "Languages: " or "Cloud Technologies: "
    const noCategory = /^[^:]{2,50}:\s*/.test(noBullet)
      ? noBullet.replace(/^[^:]{2,50}:\s*/, '')
      : noBullet;
    // Split by comma
    for (const part of noCategory.split(/,\s*/)) {
      const cleaned = part.trim().replace(/\s+/g, ' ');
      if (cleaned.length >= 2 && cleaned.length <= 60 && !/^(and|or|etc\.?)$/i.test(cleaned)) {
        items.push(cleaned);
      }
    }
  }
  return [...new Set(items)];
}

/** Heuristic: detect a flat list of short items — skills / technologies / tools */
function isSkillsList(lines: string[], sectionTitle?: string): boolean {
  const titleHint = isSkillsSectionTitle(sectionTitle);
  const nonEmpty = lines.filter(l => l.trim());
  if (nonEmpty.length < 3) return false;
  const hasBullets = nonEmpty.some(l => isBulletLine(l));
  if (hasBullets) return false; // already has bullets — render normally
  const hasLongLine = nonEmpty.some(l => l.trim().length > 60);
  if (hasLongLine) return false; // long descriptive lines → not a plain skills list
  const hasDateLike = nonEmpty.some(l => /\b(19|20)\d{2}\b/.test(l));
  if (hasDateLike) return false;
  const avgLen = nonEmpty.reduce((s, l) => s + l.trim().length, 0) / nonEmpty.length;
  return titleHint || (avgLen <= 35 && nonEmpty.length >= 5);
}

/**
 * Renders section content with bullets, numbered lists, and markdown formatting.
 * Intentionally carries NO hardcoded font-family, font-size, line-height, or color —
 * those are all inherited from the parent .resume-preview--{template} CSS rules so
 * each template can fully control the typography of the body content.
 */
export function SectionContentWithFormatting({ content, className = '', sectionTitle }: SectionContentWithFormattingProps) {
  const rawContent = content || '';

  // Only run the work-experience parser for JSON-serialized content (form editor output).
  // LLM-generated content already has proper markdown formatting (• bullets, **bold** titles,
  // dates on their own lines) — re-parsing it mangles the structure.
  const isLLMFormatted = /^[\s•*\-]|\*\*/.test(rawContent.trim()) && rawContent.includes('•');
  const contentToRender = isWorkExperienceSection(sectionTitle) && !isLLMFormatted
    ? workExperienceEntriesToDisplayText(parseWorkExperienceContent(rawContent))
    : rawContent;
  const cleaned = preprocessSectionContentForPreview(contentToRender);
  const normalized = cleaned.replace(/\r\n/g, '\n').replace(/\r/g, '\n');
  const lines = normalized.split(/\n/);

  // Contact / Personal Information: strip bullets, expand concatenated fields
  if (isContactSection(sectionTitle)) {
    const contactLines = lines.flatMap(line => {
      if (!line.trim()) return [''];
      return expandContactLine(line);
    });
    return (
      <div className={`resume-preview-content break-words overflow-visible ${className}`.trim()}>
        {contactLines.map((line, i) => {
          if (!line.trim()) return <div key={i} className="resume-preview-blank-line" aria-hidden />;
          return (
            <div key={i} className="resume-preview-text-row">
              {renderInlineFormatting(line, `C${i}`)}
            </div>
          );
        })}
      </div>
    );
  }

  // Skills/tech sections — render as clean text rows (NO chips/tags).
  // Bucketed output (### heading) → bold category label + comma-separated skills text.
  // Flat output → plain comma-separated or "Category: Skills" rows.
  if (isSkillsSectionTitle(sectionTitle)) {
    type SkillBucket = { heading: string; skills: string[] };
    const buckets: SkillBucket[] = [];
    let current: SkillBucket | null = null;

    for (const rawLine of rawContent.split('\n')) {
      const line = rawLine.trim();
      if (!line) continue;

      if (/^#{1,3}\s+/.test(line)) {
        // ### Category heading
        if (current) buckets.push(current);
        current = { heading: line.replace(/^#+\s*/, '').trim(), skills: [] };
      } else if (/^[A-Za-z &\/]+:\s+\S/.test(line) && !isBulletLine(line)) {
        // "Category Label: Skill1, Skill2" style
        const colonIdx = line.indexOf(':');
        const heading = line.slice(0, colonIdx).trim();
        const rest = line.slice(colonIdx + 1).trim();
        if (current) buckets.push(current);
        current = { heading, skills: rest.split(/,\s*/).map(s => s.trim()).filter(s => s.length >= 2) };
      } else if (current) {
        const items = line
          .replace(/^[•\-*]\s*/, '')
          .split(/,\s*/)
          .map(s => s.trim())
          .filter(s => s.length >= 2 && s.length <= 60);
        current.skills.push(...items);
      } else {
        // Skills before any heading
        const items = line.replace(/^[•\-*]\s*/, '').split(/,\s*/).map(s => s.trim()).filter(s => s.length >= 2);
        if (items.length > 0) {
          if (!current) current = { heading: '', skills: [] };
          current.skills.push(...items);
        }
      }
    }
    if (current && (current.skills.length > 0 || current.heading)) buckets.push(current);

    if (buckets.length > 0) {
      return (
        <div className={`resume-preview-content break-words overflow-visible ${className}`.trim()}>
          {buckets.map((bucket, bi) => (
            <div key={bi} className="mb-1.5">
              {bucket.heading ? (
                <div className="resume-preview-body-row">
                  <span style={{ fontWeight: 600 }}>{bucket.heading}:</span>{' '}
                  <span style={{ fontWeight: 400 }}>{bucket.skills.join(', ')}</span>
                </div>
              ) : (
                <div className="resume-preview-body-row">
                  {bucket.skills.join(', ')}
                </div>
              )}
            </div>
          ))}
        </div>
      );
    }

    // Flat fallback — just render as plain text rows
    const items = parseSkillsToItems(rawContent);
    if (items.length > 0) {
      return (
        <div className={`resume-preview-content break-words overflow-visible ${className}`.trim()}>
          <div className="resume-preview-body-row">{items.join(', ')}</div>
        </div>
      );
    }
  }

  // Skills-like flat lists — render as plain comma-separated text, not chips
  // Skip for certifications, references, education — they must render as normal text rows
  const isCertOrRefSection = !!(sectionTitle && /certif|reference|education|award|achievement/i.test(sectionTitle));
  if (!isCertOrRefSection && isSkillsList(lines, sectionTitle)) {
    const items = lines.map(l => l.trim()).filter(Boolean);
    return (
      <div className={`resume-preview-content break-words overflow-visible ${className}`.trim()}>
        <div className="resume-preview-body-row">{items.join(', ')}</div>
      </div>
    );
  }

  return (
    <div className={`resume-preview-content break-words overflow-visible ${className}`.trim()}>
      {lines.map((line, i) => {
        const trimmed = line.trim();
        if (!trimmed) {
          return <div key={i} className="resume-preview-blank-line" aria-hidden />;
        }
        // Markdown subheadings (### or ##) — small bold category headers
        if (/^#{2,3}\s+/.test(trimmed)) {
          const headingText = trimmed.replace(/^#{2,3}\s*/, '').trim();
          return (
            <div
              key={i}
              className="text-[10px] font-bold uppercase tracking-wide mt-2 mb-0.5"
              style={{ color: 'var(--resume-accent, #4f46e5)', letterSpacing: '0.06em' }}
            >
              {headingText}
            </div>
          );
        }
        // Experience role header: "**Job Title** | Company, Location" — render with visual weight
        // A line is a role header if it starts with ** and contains | or · separators
        if (/^\*\*[^*]+\*\*/.test(trimmed) && /[|·]/.test(trimmed)) {
          return (
            <div key={i} className="resume-preview-text-row" style={{ fontWeight: 600, marginTop: i > 0 ? '0.6em' : 0 }}>
              {renderInlineFormatting(trimmed, `L${i}`)}
            </div>
          );
        }
        // Date line: "Jan 2020 – Present" or "01/2019 – 04/2023" — render muted
        if (/^(\d{1,2}[\/\s]\d{4}|\w{3}\s+\d{4})\s*[–\-—]/.test(trimmed)) {
          return (
            <div key={i} className="resume-preview-text-row" style={{ opacity: 0.65, fontSize: '0.85em', marginBottom: '0.15em' }}>
              {trimmed}
            </div>
          );
        }
        if (isBulletLine(line)) {
          return (
            <div key={i} className="resume-preview-body-row resume-preview-bullet-row">
              <span className="resume-preview-bullet">•</span>
              <span className="resume-preview-bullet-text">{renderInlineFormatting(bulletContent(line), `L${i}`)}</span>
            </div>
          );
        }
        if (isNumberedLine(line)) {
          const dotMatch = line.match(/^\s*(\d+)\.\s+(.*)$/);
          const parenMatch = line.match(/^\s*(\d+)\)\s+(.*)$/);
          const num = dotMatch ? dotMatch[1] : parenMatch ? parenMatch[1] : '1';
          const restRaw = dotMatch ? dotMatch[2] : parenMatch ? parenMatch[2] : line.replace(/^\s*\d+[.)]\s*/, '');
          const rest = restRaw.trim();
          return (
            <div key={i} className="resume-preview-body-row resume-preview-num-row">
              <span className="resume-preview-bullet">{num}.</span>
              <span className="resume-preview-bullet-text">{renderInlineFormatting(rest, `L${i}`)}</span>
            </div>
          );
        }
        // preserve source indentation as data-driven inline padding only
        const indent = line.match(/^(\s+)/)?.[1].length ?? 0;
        const indentStyle = indent >= 2 ? { paddingLeft: '1.5em' } : indent >= 1 ? { paddingLeft: '0.75em' } : undefined;
        return (
          <div key={i} className="resume-preview-text-row" style={indentStyle}>
            {renderInlineFormatting(trimmed, `L${i}`)}
          </div>
        );
      })}
    </div>
  );
}

/** Split long section content into blocks for card layout. Double-newline or ~600 chars. */
export function splitSectionIntoBlocks(content: string, maxBlockChars = 600): string[] {
  if (!content?.trim()) return [];
  const normalized = content.replace(/\r\n/g, '\n').replace(/\r/g, '\n').trim();
  const byDoubleNewline = normalized.split(/\n\n+/);
  const blocks: string[] = [];
  for (const block of byDoubleNewline) {
    const trimmed = block.trim();
    if (!trimmed) continue;
    if (trimmed.length <= maxBlockChars) {
      blocks.push(trimmed);
    } else {
      const lines = trimmed.split(/\n/);
      let current = '';
      for (const line of lines) {
        if (current.length + line.length + 1 > maxBlockChars && current.length > 0) {
          blocks.push(current.trim());
          current = line;
        } else {
          current = current ? current + '\n' + line : line;
        }
      }
      if (current.trim()) blocks.push(current.trim());
    }
  }
  return blocks.length > 0 ? blocks : [normalized];
}
